import {
	type AnchorPlacement,
	type CollectionFieldAnchorManualFieldValue,
	type CollectionModelFieldAnchorValue,
} from '@ikegps/core-api';
import {
	CollectionModelFieldAnchor,
	CollectionModelFieldSubFormInstance,
	CollectionModelFieldVector,
	truthy,
} from '@ikegps/core-shared';
import { comparer, override, makeObservable } from 'mobx';
import { T, Translate } from '../../i18n';
import { BearingInputState } from './bearing.input.state';
import { DistanceInputState } from './distance.input.state';
import { type SelectInputOption, SelectInputState } from './input.select';
import { InputState } from './input.state';

export class AnchorInputState extends InputState<CollectionModelFieldAnchorValue> {
	distance: DistanceInputState;
	bearing: BearingInputState;
	pitch: number | null = null;
	placement: SelectInputState<AnchorPlacement>;
	relatedSpans: [SelectInputState<string>, SelectInputState<string>];
	cachedSpanFields: CollectionModelFieldVector[] = [];

	// Whether the user has touched placement / related spans / bearing. An anchor uploaded from
	// IKE Field can land in a placement-without-spans state that the UI rules would not allow;
	// per the spec it must not surface a blocking "select a span" error until the user interacts.
	private interacted = false;

	@override override get value(): CollectionFieldAnchorManualFieldValue | null {
		const placement = this.placement.value;
		const relatedSpanIds = this.relatedSpanIds();

		// For Inline/Bisect the bearing is derived from the related spans (the domain model is the
		// single source of truth); Other / unset placements use the manually-entered bearing.
		const bearing = this.deriveBearing() ?? this.bearing.value;

		if (this.distance.value == null || bearing == null) {
			return null;
		}

		return {
			bearing: bearing,
			distance: this.distance.value,
			pitch: this.pitch as number,
			collectionTool: 'VECTOR',
			placement: placement as AnchorPlacement,
			relatedSpanIds,
		};
	}

	static equal(value1: CollectionFieldAnchorManualFieldValue, value2: CollectionFieldAnchorManualFieldValue) {
		if (value1 === value2) {
			return true;
		}

		if (value1 == null || value2 == null) {
			return false;
		}

		return (
			DistanceInputState.equal(value1.distance, value2.distance) &&
			BearingInputState.equal(value1.bearing, value2.bearing) &&
			value1.pitch === value2.pitch &&
			value1.placement === value2.placement
		);
	}

	constructor(private field: CollectionModelFieldAnchor) {
		super(field.schema.displayName);

		makeObservable(this);

		this.isLabelShown = false;

		this.distance = new DistanceInputState('Distance');
		this.bearing = new BearingInputState('Bearing');
		this.placement = new SelectInputState('Placement');

		this.placement.setOptions([
			{
				key: Translate(T.ANCHOR_ALIGNMENT_INLINE),
				value: 'Inline',
			},
			{
				key: Translate(T.ANCHOR_ALIGNMENT_BISECT),
				value: 'Bisect',
			},
			{
				key: Translate(T.ANCHOR_ALIGNMENT_OTHER),
				value: 'Other',
			},
		]);

		this.relatedSpans = [
			new SelectInputState(Translate(T.ACCURATE_ANCHOR_RELATED_SPAN_TITLE, 'A')),
			new SelectInputState(Translate(T.ACCURATE_ANCHOR_RELATED_SPAN_TITLE, 'B')),
		];

		// User typing into the bearing field is treated as a custom bearing — flip placement to
		// 'Other' and clear span links (spec: manually modifying the bearing forces 'Other').
		this.reaction(
			() => this.bearing.value,
			() => {
				if (this.bearing.focused) {
					this.interacted = true;
					this.placement.setValue('Other');
					this.relatedSpans.forEach((x) => x.setValue(null));
				}
			}
		);

		// Keep the A/B option lists mutually exclusive — whenever either selection changes, refresh
		// both option lists so the chosen span disappears from the other.
		this.reaction(
			() => [this.relatedSpans[0].value, this.relatedSpans[1].value],
			() => {
				if (this.relatedSpans.some((x) => x.focused)) {
					this.interacted = true;
				}
				this.refreshRelatedSpansOptions();
			},
			{ equals: comparer.shallow }
		);

		// Placement drives which related-span dropdowns are enabled. Applied here on change AND
		// synchronously inside setValue (below) so the state is correct on the very first render
		// when a saved anchor loads — a change-only reaction was the UFO-531 disabled-on-load bug.
		this.reaction(
			() => this.placement.value,
			(placement) => {
				if (this.placement.focused) {
					this.interacted = true;
				}
				this.applyPlacementAvailability(placement);
			}
		);

		// Keep the displayed bearing in sync with the related spans for Inline/Bisect, and surface
		// the incomplete-selection error once the user has interacted.
		this.reaction(
			() => [this.placement.value, this.relatedSpans[0].value, this.relatedSpans[1].value],
			() => {
				if (!this.bearing.focused) {
					const derived = this.deriveBearing();
					if (derived != null && derived !== this.bearing.value) {
						this.bearing.setValue(derived);
					}
				}
				this.refreshError();
			},
			{ equals: comparer.shallow }
		);

		this.applyPlacementAvailability(this.placement.value);
	}

	/** The selected related-span field ids, truncated to what the current placement allows. */
	private relatedSpanIds(): string[] {
		return this.relatedSpans
			.map((x) => x.value)
			.slice(0, CollectionModelFieldAnchor.placementSpanCount(this.placement.value))
			.filter(truthy);
	}

	/** The bearing implied by the current placement + related spans, or null when not derivable. */
	private deriveBearing(): number | null {
		return CollectionModelFieldAnchor.calculateBearing(
			this.placement.value,
			CollectionModelFieldAnchor.spanBearings(this.field.collection, this.relatedSpanIds())
		);
	}

	/** Enable/disable each related-span dropdown per placement, clearing values that no longer apply. */
	private applyPlacementAvailability(placement: AnchorPlacement | null) {
		const spanCount = CollectionModelFieldAnchor.placementSpanCount(placement);

		this.relatedSpans[0].setDisabled(spanCount < 1);
		this.relatedSpans[1].setDisabled(spanCount < 2);

		if (spanCount < 1 && this.relatedSpans[0].value != null) {
			this.relatedSpans[0].setValue(null);
		}
		if (spanCount < 2 && this.relatedSpans[1].value != null) {
			this.relatedSpans[1].setValue(null);
		}
	}

	private refreshError() {
		const placement = this.placement.value;
		const spanCount = this.relatedSpanIds().length;

		if (this.interacted && placement === 'Inline' && spanCount < 1) {
			this.setError(T.ACCURATE_ANCHOR_RELATED_SPAN_NO_SELECTION_INLINE);
		} else if (this.interacted && placement === 'Bisect' && spanCount < 2) {
			this.setError(T.ACCURATE_ANCHOR_RELATED_SPAN_NO_SELECTION_BISECT);
		} else {
			this.setError(null);
		}
	}

	public populateRelatedSpansOptions(fields: CollectionModelFieldVector[]) {
		this.cachedSpanFields = fields;
		this.refreshRelatedSpansOptions();
	}

	private sameOptions(a: readonly SelectInputOption[], b: readonly SelectInputOption[]): boolean {
		if (a.length !== b.length) return false;
		for (let i = 0; i < a.length; i++) {
			if (a[i]!.value !== b[i]!.value || a[i]!.key !== b[i]!.key) return false;
		}
		return true;
	}

	/**
	 * Rebuild the option list for each related-span dropdown, excluding the option already chosen in
	 * the *other* dropdown so a span can't be picked as both A and B.
	 *
	 * Only writes to `select.options` when the list actually differs — otherwise the render autorun
	 * (`reaction:root` in render.service.ts) sees an option-array change on every redraw and
	 * ping-pongs into a "doesn't converge" failure.
	 */
	private refreshRelatedSpansOptions() {
		const baseOptions: SelectInputOption[] = [];

		this.cachedSpanFields.forEach((x) => {
			// Skip any vector fields on the same subform level & type
			// @TODO update logic to use anchor base form when available
			if (x.parent && x.parent.schema.id !== this.field.parent.schema.id) {
				const spanLabel =
					x.parent instanceof CollectionModelFieldSubFormInstance ? x.parent.title : x.schema.displayName;

				baseOptions.push({
					key: spanLabel ?? Translate(T.UNKNOWN),
					value: x.id,
				});
			}
		});

		const otherSelections = [this.relatedSpans[1].value, this.relatedSpans[0].value];
		this.relatedSpans.forEach((select, i) => {
			const exclude = otherSelections[i];
			const filtered = exclude ? baseOptions.filter((o) => o.value !== exclude) : baseOptions;
			if (!this.sameOptions(select.options, filtered)) {
				select.setOptions(filtered);
			}
		});
	}

	@override override setValue(val: CollectionFieldAnchorManualFieldValue) {
		if (val == null) {
			this.distance.setValue(null);
			this.bearing.setValue(null);
			this.pitch = null;
			this.placement.setValue(null);
			this.relatedSpans[0].setValue(null);
			this.relatedSpans[1].setValue(null);
			this.applyPlacementAvailability(null);
			this.refreshRelatedSpansOptions();
			this.setError(null);
			return;
		}

		this.distance.setValue(val.distance);
		this.pitch = val.pitch;
		this.placement.setValue(val.placement);

		// Truncate related spans to what this placement allows
		const spanCount = CollectionModelFieldAnchor.placementSpanCount(val.placement);
		this.relatedSpans[0].setValue(spanCount >= 1 ? (val.relatedSpanIds?.[0] ?? null) : null);
		this.relatedSpans[1].setValue(spanCount >= 2 ? (val.relatedSpanIds?.[1] ?? null) : null);

		// Apply the enabled/disabled state synchronously (not via the placement reaction) so a
		// loaded anchor renders with the correct dropdown state on its first frame.
		this.applyPlacementAvailability(val.placement);
		this.refreshRelatedSpansOptions();

		// Inline/Bisect bearings always reflect the current spans; fall back to the stored value.
		this.bearing.setValue(this.deriveBearing() ?? val.bearing);
	}

	override hasError() {
		return (
			super.hasError() ||
			this.distance.hasError() ||
			this.bearing.hasError() ||
			this.placement.hasError() ||
			this.relatedSpans.some((x) => x.hasError())
		);
	}

	@override override get error() {
		// Don't show an error message if one is already being shown
		if (this.distance.hasError() || this.bearing.hasError() || this.placement.hasError()) {
			return '';
		}

		if (!this.distance.value) {
			// distance is empty
			if (this.placement.value || (this.bearing.value && this.bearing.value > 0)) {
				// placement has a value or the bearing has an actual value
				return T.DISTANCE_MISSING;
			}
			return this._error; // everything is empty
		} else if (this.distance.value && this.bearing.value === null) {
			// does bearing have an actual value
			return T.BEARING_MISSING;
		} else if (this.placement.value === null && this.distance.value) {
			// does placement have a valid value
			return T.PLACEMENT_MISSING;
		}

		// everything has a valid value
		return this._error;
	}

	override set error(msg: string) {
		this._error = msg || '';
	}

	override destroy() {
		super.destroy();
		this.distance.destroy();
		this.bearing.destroy();
		this.placement.destroy();
		this.relatedSpans.forEach((x) => x.destroy());
	}
}
